/**
 * Sample bar data generator — DEV ONLY.
 *
 * Phase A scaffold so we can test the TradeChart component without a real
 * data source wired up. Generates deterministic-but-varied OHLCV bars with
 * a believable random walk so the chart looks like real price action.
 *
 * In Phase B this file becomes optional: real bars come from the data
 * source layer (NinjaTrader, Tradovate, CSV, etc). Keep it around for
 * offline UI development.
 */

import { getExecutionInstantMs, getCurrentTradingDay, SESSION_OPEN_HOUR, SESSION_CLOSE_HOUR } from '../services/timeService';

const TIMEFRAME_SECONDS = {
  '1m': 60,
  '5m': 300,
  '15m': 900,
  '1h': 3600,
};

// Mulberry32 — small, deterministic PRNG so the same seed produces the same
// bars. Lets us tie sample data to a trade id so the chart looks consistent
// when the user clicks around.
const seededRandom = (seed) => {
  let s = seed | 0;
  return () => {
    s = (s + 0x6d2b79f5) | 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

const stringHash = (s) => {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
};

/**
 * Generate a session of OHLCV bars.
 *
 * @param {Object} opts
 * @param {string} opts.symbol         e.g. 'MNQ' (used for seeding + base price)
 * @param {string} opts.dateStr        YYYY-MM-DD trading day (futures session anchor)
 * @param {string} opts.timeframe      '1m' | '5m' | '15m' | '1h'
 * @param {number} opts.basePrice      Approximate price level (e.g. 27800 for MNQ)
 * @param {number} opts.volatility     Per-bar std dev as a fraction (e.g. 0.0008)
 * @param {string} opts.seedKey        Anything that makes this series stable
 *                                      (e.g. trade.id) so re-mounts don't reshuffle.
 * @returns {Array<{time, open, high, low, close, volume}>}
 */
export const generateSampleSession = ({
  symbol = 'MNQ',
  dateStr,
  timeframe = '5m',
  basePrice,
  volatility = 0.0008,
  seedKey = ''
} = {}) => {
  const tfSec = TIMEFRAME_SECONDS[timeframe] || 300;

  // Session anchors via timeService — futures open 6pm ET prior calendar day,
  // close 5pm ET on the trading day. getExecutionInstantMs produces ET-anchored
  // ms instants so the synthesized bars line up correctly regardless of host TZ.
  const tradingDay = dateStr || getCurrentTradingDay();
  const openTime  = `${String(SESSION_OPEN_HOUR).padStart(2, '0')}:00`;   // "18:00" — rolls to prior calendar day
  const closeTime = `${String(SESSION_CLOSE_HOUR).padStart(2, '0')}:00`;  // "17:00" — same trading day
  const sessionStartMs = getExecutionInstantMs(tradingDay, openTime);
  const sessionEndMs   = getExecutionInstantMs(tradingDay, closeTime);

  const totalSec = Math.max(tfSec, Math.floor((sessionEndMs - sessionStartMs) / 1000));
  const barCount = Math.max(10, Math.floor(totalSec / tfSec));

  // Default base prices for common symbols
  const defaults = { MNQ: 27800, NQ: 27800, MES: 5780, ES: 5780, MCL: 75, CL: 75, GC: 2680, MGC: 2680 };
  const base = basePrice || defaults[symbol?.toUpperCase()] || 100;

  const rand = seededRandom(stringHash(`${symbol}|${dateStr}|${timeframe}|${seedKey}`));

  // Random walk with mean reversion to keep prices near `base`
  const bars = [];
  let close = base;
  let lastClose = base;

  for (let i = 0; i < barCount; i++) {
    const time = Math.floor(sessionStartMs / 1000) + i * tfSec;
    const meanRevert = (base - close) * 0.01; // pull toward base
    const drift = (rand() - 0.5) * 2 * close * volatility;
    const move = meanRevert + drift;

    const open = lastClose;
    close = lastClose + move;
    // Realistic intrabar wicks
    const wickRange = Math.abs(move) + close * volatility * 0.6;
    const high = Math.max(open, close) + rand() * wickRange;
    const low = Math.min(open, close) - rand() * wickRange;
    const volume = Math.floor(500 + rand() * 4500);

    bars.push({
      time,
      open: Number(open.toFixed(2)),
      high: Number(high.toFixed(2)),
      low: Number(low.toFixed(2)),
      close: Number(close.toFixed(2)),
      volume,
    });
    lastClose = close;
  }

  return bars;
};

/**
 * Build a sample trade-with-bars bundle from an existing trade record.
 * Looks up base price from the trade's entry, uses trade.date for the session.
 * Useful in Phase A so any trade in the journal can render a reasonable chart.
 */
export const sampleBundleFromTrade = (trade, timeframe = '5m') => {
  if (!trade) return { bars: [], entries: [], exits: [] };
  const entryPrice = parseFloat(trade.entry) || parseFloat(trade.entries?.[0]?.price);
  const basePrice = isFinite(entryPrice) ? entryPrice : undefined;
  const bars = generateSampleSession({
    symbol: trade.symbol,
    dateStr: trade.date,
    timeframe,
    basePrice,
    seedKey: trade.id,
  });
  return { bars };
};
